import { Singleton } from '@visiba/instantiation';
import { I18nAndL10n } from '@modules/i18n_and_l10n/mod';
import type { PublicBaseApiModel } from '@src/api/models/PublicBaseApiModel';

interface StoredData {
	languageCode: I18nAndL10n.AvailableVisibaLocales | null;
	rootUnitId: number | null;
	alias: string | null;
	id: number | null;
	globalCustomerId: string | null;
	enforcedWhiteLabel: boolean | null;
}

export class CriticalMissingConfigError extends Error {
	constructor(public message: string) {
		super(message);
		this.name = 'CriticalMissingConfigError';
		Object.setPrototypeOf(this, CriticalMissingConfigError.prototype);
	}
}

@Singleton()
export class LicenceHolderService {
	public get alias(): string {
		if (this.storedData.alias == null) {
			throw new CriticalMissingConfigError("Licenceholder's alias was never set");
		}

		return this.storedData.alias;
	}
	public get id(): number {
		if (this.storedData.id == null) {
			throw new CriticalMissingConfigError("Licenceholder's id was never set");
		}

		return this.storedData.id;
	}

	public get rootUnitId(): number {
		if (this.storedData.rootUnitId == null) {
			throw new CriticalMissingConfigError("Licenceholder's rootUnitId was never set");
		}

		return this.storedData.rootUnitId;
	}

	public get globalCustomerId(): string {
		if (this.storedData.globalCustomerId == null) {
			throw new CriticalMissingConfigError("Licenceholder's globalCustomerId was never set");
		}

		return this.storedData.globalCustomerId;
	}

	public get rootLanguageCode(): I18nAndL10n.AvailableVisibaLocales {
		if (this.storedData.languageCode == null) throw new CriticalMissingConfigError("Licenceholder's languageCode was never set");

		return this.storedData.languageCode;
	}

	public get enforcedWhiteLabel(): boolean {
		if (this.storedData.enforcedWhiteLabel == null) throw new CriticalMissingConfigError('EnforcedWhiteLabel was never set');

		return this.storedData.enforcedWhiteLabel;
	}
	private storedData: StoredData = {
		languageCode: null,
		rootUnitId: null,
		alias: null,
		id: null,
		globalCustomerId: null,
		enforcedWhiteLabel: null,
	};

	constructor() {
		// Empty
	}

	public async loadFromAlias(alias: string, data: PublicBaseApiModel): Promise<void> {
		const { LicenceHolderId, RootUnitId, LanguageCode, GlobalCustomerId, HideProvidedByVisibaCare } = data;

		this.storedData = {
			alias,
			languageCode: I18nAndL10n.convertExternalLocaleToAvailableLocale(LanguageCode),
			rootUnitId: RootUnitId,
			id: LicenceHolderId,
			globalCustomerId: GlobalCustomerId,
			enforcedWhiteLabel: HideProvidedByVisibaCare,
		};
	}
}
